import { Box, Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import { TextFilter } from "../../components/common/TextFilter";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { USER_ROLES } from "../../helpers/constants";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useAgreements } from "../../services/agreementsService";
import TableService from "../../services/tableService";
import { AgreementConfigurationDialog } from "./AgreementConfigurationDialog/AgreementConfigurationDialog";
import { AGREEMENTS_TABLE_NAME, useAgreementsTableFieldsConfig } from "./AgreementsPageTableConfig";

export const AgreementsPage = (props) => {
    const { pageName } = props;
    const { t } = useTranslation();
    const { hasAccess } = useAccessControl([
        USER_ROLES.ADMIN,
        USER_ROLES.SALES_MANAGER,
        USER_ROLES.SALES,
        USER_ROLES.SALES_LEGAL,
        USER_ROLES.INVESTMENT_DPI
    ]);
    const { openDialog } = useDialogBetter();

    const [dataLocal, setDataLocal] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState();
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [checkedAll, setCheckedAll] = useState(false);

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const {
        data: agreementsData,
        isLoading: isAgreementsLoading,
        isFetching,
        refetch,
    } = useAgreements(searchParams, {
        select: (data) => data,
    });

    useEffect(() => {
        if (agreementsData?.results) {
            setDataLocal(agreementsData.results);
            setRecordsCount(agreementsData.count);
        }
    }, [agreementsData]);

    const agreementsTableFieldsConfig = useAgreementsTableFieldsConfig();

    const exportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            agreementsTableFieldsConfig,
            hiddenColumnsForTables?.[AGREEMENTS_TABLE_NAME],
            columnsOrdersForTables?.[AGREEMENTS_TABLE_NAME],
        );
    }, [
        hiddenColumnsForTables?.[AGREEMENTS_TABLE_NAME],
        columnsOrdersForTables?.[AGREEMENTS_TABLE_NAME],
        agreementsTableFieldsConfig,
    ]);

    const onClickButton = useCallback((e) => {
        e.stopPropagation();
    }, []);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                agreementsTableFieldsConfig,
                dataLocal,
                hiddenColumnsForTables?.[AGREEMENTS_TABLE_NAME]
                    ? hiddenColumnsForTables?.[AGREEMENTS_TABLE_NAME]
                    : [],
                columnsOrdersForTables?.[AGREEMENTS_TABLE_NAME]
                    ? columnsOrdersForTables?.[AGREEMENTS_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            agreementsTableFieldsConfig,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    const handleOpenAgreementConfigurationDialog = () => {
        openDialog({
            Component: AgreementConfigurationDialog,
            componentProps: {},
        });
    };

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.agreements")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "start",
                            width: "40%",
                            marginLeft: "50px",
                        }}
                    >
                        <TextFilter
                            filterKey={"customer"}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            resetPageNumber={resetPageNumber}
                            withPagination
                            placeholder={t("clients.filter.masterFilterPlaceholder") ?? undefined}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={{
                            name: AGREEMENTS_TABLE_NAME,
                            config: agreementsTableFieldsConfig,
                        }}
                        filteringData={() => {}}
                        refreshTable={refetch}
                        // @ts-ignore
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={true}
                        showMyFiltersButton={true}
                        showSearchButton={true}
                        itemType={"agreement"}
                        style={{ marginBlock: "5px" }}
                        showCreateButton={hasAccess}
                        onClickCreateItem={handleOpenAgreementConfigurationDialog}
                        mdGridButton={hasAccess ? 1.25 : 2.75}
                        showImportList={false}
                        showRemoveLeadButton={false}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={isAgreementsLoading && !dataLocal.length}>
                        <FilterTable
                            // @ts-ignore
                            data={data}
                            countRecords={recordsCount}
                            resetPageNumber={resetPageNumber}
                            selectedItemId={selectedRowId}
                            style={{ maxHeight: "60vh" }}
                            headersConfig={TableService.orderColumns(
                                TableService.hideHiddenColumns(
                                    agreementsTableFieldsConfig,
                                    hiddenColumnsForTables?.[AGREEMENTS_TABLE_NAME]
                                        ? hiddenColumnsForTables?.[AGREEMENTS_TABLE_NAME]
                                        : [],
                                ),
                                columnsOrdersForTables?.[AGREEMENTS_TABLE_NAME]
                                    ? columnsOrdersForTables?.[AGREEMENTS_TABLE_NAME]
                                    : [],
                            )}
                            showCleanFilterIcon={true}
                            showContextMenu={false}
                            showExportToFileButton={true}
                            exportToFileUrl={"agreements/export/"}
                            exportToFileSearchParams={searchParams}
                            exportToFileHeaders={exportHeaders}
                            exportToFileFileName={`${t("agreements.exportFileName")}.xlsx`}
                            showDetailsIcon={false}
                            showCheckbox={false}
                            checkedAll={checkedAll}
                            page={dataPage}
                            onPageChange={handleChangeDataPage}
                            rowsPerPage={dataPageSize}
                            onRowsPerPageChange={handleChangeDataPageSize}
                            setDataLocal={setDataLocal}
                            setCheckedAll={setCheckedAll}
                            setSelectedRowId={setSelectedRowId}
                            isLoading={isFetching}
                        />
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
};
